import { ArrowLeft, Pencil, Send, Loader2, AlertTriangle } from "lucide-react";

/**
 * Bottom-sheet modal that lets the caregiver review the full report
 * before submitting. Read-only render of the checklist + AI-formatted
 * (or raw) clinical note, with a "keep editing" escape hatch.
 */
export default function PreviewSubmitModal({
  client,
  shiftType,
  checklist,
  items,
  rawNotes,
  formattedNotes,
  incident,
  activeShift,
  clientId,
  submitting,
  onClose,
  onSubmit,
}) {
  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      onClick={() => !submitting && onClose()}
      data-testid="preview-modal"
    >
      <div
        className="bg-white sm:rounded-2xl border-t sm:border border-clinical-border w-full sm:max-w-xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white flex items-center justify-between px-5 py-4 border-b border-clinical-border">
          <div>
            <div className="label-eyebrow">Review before submit</div>
            <h3 className="font-heading text-lg font-medium">{client.name}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="btn-ghost"
            data-testid="preview-close"
          >
            <ArrowLeft className="h-4 w-4" /> Edit
          </button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          <div className="flex flex-wrap items-center gap-2 text-xs text-clinical-subtext">
            <span className="pill bg-clinical-bg">{new Date().toLocaleDateString()}</span>
            <span className="pill bg-clinical-bg capitalize">{shiftType} shift</span>
            {incident && (
              <span className="pill !bg-destructive/10 !text-destructive">
                <AlertTriangle className="h-3 w-3" /> Incident flagged
              </span>
            )}
            {activeShift && activeShift.client_id === clientId && (
              <span className="pill !bg-emerald-100 !text-emerald-700">
                Clocks out on submit
              </span>
            )}
          </div>

          <div>
            <div className="label-eyebrow mb-2">Care checklist</div>
            <div className="grid grid-cols-2 gap-2">
              {items.map((it) => {
                const Icon = it.icon;
                const on = !!checklist[it.key];
                return (
                  <div
                    key={it.key}
                    className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 ${
                      on
                        ? "border-clinical-primary/40 bg-clinical-primary/5 text-clinical-text"
                        : "border-clinical-border bg-clinical-bg text-clinical-subtext"
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    <span className="text-xs">{it.label}</span>
                    <span className="ml-auto text-xs">{on ? "✓" : "—"}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <div className="label-eyebrow mb-1">Clinical note</div>
            <div
              className="whitespace-pre-wrap rounded-lg border border-clinical-border bg-clinical-bg p-3 text-clinical-text leading-relaxed"
              data-testid="preview-clinical-note"
            >
              {formattedNotes || rawNotes}
            </div>
            {formattedNotes && formattedNotes !== rawNotes && (
              <div className="mt-2 text-[11px] text-clinical-subtext">
                Raw caregiver input was rewritten by AI. You can still go back and edit either version.
              </div>
            )}
          </div>

          {rawNotes && formattedNotes && formattedNotes !== rawNotes && (
            <details className="rounded-lg border border-clinical-border bg-white p-3">
              <summary className="cursor-pointer text-xs text-clinical-subtext">
                Show original (raw) caregiver note
              </summary>
              <div className="mt-2 whitespace-pre-wrap text-xs text-clinical-subtext">
                {rawNotes}
              </div>
            </details>
          )}
        </div>
        <div className="sticky bottom-0 bg-white flex flex-col-reverse sm:flex-row items-stretch sm:items-center sm:justify-end gap-2 px-5 py-4 border-t border-clinical-border">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="btn-secondary"
            data-testid="preview-edit"
          >
            <Pencil className="h-4 w-4" /> Keep editing
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={submitting}
            className="btn-primary"
            data-testid="preview-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
              </>
            ) : (
              <>
                <Send className="h-4 w-4" /> Confirm &amp; submit
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
